import { faEye, faEyeSlash, faLock } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AlertTitle, Box, Button, Checkbox, CircularProgress, IconButton, InputAdornment, Table, TableBody, TableCell, TableRow, TextField, TextFieldProps, Typography, styled } from "@mui/material";
import { grey } from "@mui/material/colors";
import { Dispatch, FC, HTMLInputTypeAttribute, ReactNode, SetStateAction, useCallback, useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PostCommunityGroupRegistration } from "../api/dtos/dtos";
import Center from "../components/common/Center";
import Hr from "../components/common/HorizontalRule";
import { UserFormatError } from "../constants/exceptions/CustomExceptions";
import { ERROR_TIMEOUT, SUCCESS_TIMEOUT } from "../constants/styles/AlertConstants";
import AuthContext from "../contexts/auth/AuthContext";
import { SpeciesContext } from "../contexts/data/SpeciesContext";
import AlertContext from "../contexts/styles/AlertContext";
import useServiceStackClient from "../hooks/api/useServiceStackClient";

interface IStyledTableRowProps {
    primaryLabel: string;
    secondaryLabel?: string;

    type?: HTMLInputTypeAttribute;

    fieldState?: FieldState;

    select?: boolean;
    items?: ReactNode[];

    disabled?: boolean;

    value: any;
    onChange?: Dispatch<SetStateAction<any>>
}

type FieldState = {
    state?: "error" | "warning";
    message?: string;
}

const RegisterGroupContainer = styled(Box)(() => ({
    padding: '30px 14px 8px 14px'
}));

const StyledTableCell = styled(TableCell)(() => ({
    padding: '8px',
    border: 'none'
}));

const StyledTextField = styled(TextField, { shouldForwardProp: (props) => props !== 'stateColor' })<TextFieldProps & { stateColor?: string }>(({ theme, stateColor, disabled }) => ({
    '& label': {
        color: stateColor === 'error' ?
            theme.palette.error.main :
            stateColor === 'warning' ?
                theme.palette.warning.main :
                grey[700],
    },
    '& p': {
        color: stateColor === 'error' ?
            theme.palette.error.main :
            stateColor === 'warning' ?
                theme.palette.warning.main :
                grey[700]
    },
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: stateColor === 'error' ?
                `${theme.palette.error.main}77` :
                stateColor === 'warning' ?
                    theme.palette.warning.main :
                    'rgba(0, 0, 0, 0.2)',
        },
        '&:hover fieldset': {
            borderColor: stateColor === 'error' ?
                theme.palette.error.main :
                stateColor === 'warning' ?
                    theme.palette.warning.main :
                    disabled ?
                        'rgba(0, 0, 0, 0.2)' :
                        'rgb(0, 0, 0)',
        },
        '&.Mui-focused fieldset': {
            borderColor: stateColor === 'error' ?
                theme.palette.error.main :
                stateColor === 'warning' ?
                    theme.palette.warning.main :
                    theme.palette.primary.main
        },
    },
}));

const StyledTableRow: FC<IStyledTableRowProps> = ({
    primaryLabel,
    secondaryLabel,
    type,
    select,
    items,
    fieldState,
    disabled,
    value,
    onChange
}) => {
    const [showPassword, setShowPassword] = useState<boolean>(false);

    return (
        <TableRow>
            <StyledTableCell sx={{ minWidth: '124px', maxWidth: '124px' }}>
                {primaryLabel}:
            </StyledTableCell>
            <StyledTableCell width='100%'>
                {
                    select ?
                        <StyledTextField
                            select
                            stateColor={fieldState?.state}
                            color={fieldState?.state}
                            helperText={fieldState?.message}
                            disabled={!!disabled}
                            label={secondaryLabel ?? ""}
                            fullWidth
                            size='small'
                            value={value}
                            onChange={onChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        >
                            {items}
                        </StyledTextField> :
                        <StyledTextField
                            id={`Text-Field-${primaryLabel.replaceAll(' ', '-')}`}
                            inputProps={{
                                autoComplete: 'password',
                            }}
                            InputProps={{
                                endAdornment: type === 'password' ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            disableRipple
                                            sx={{ width: '24px', heigh: '24px' }}
                                            onMouseDown={() => setShowPassword(true)}
                                            onMouseUp={() => setShowPassword(false)}
                                            onMouseLeave={() => setShowPassword(false)}
                                        >
                                            {
                                                showPassword ?
                                                    <FontAwesomeIcon icon={faEye} style={{ fontSize: '1.2rem' }} /> :
                                                    <FontAwesomeIcon icon={faEyeSlash} style={{ fontSize: '1.2rem' }} />
                                            }
                                        </IconButton>
                                    </InputAdornment>
                                ) :
                                    disabled ? (
                                        <InputAdornment position="end">
                                            <FontAwesomeIcon icon={faLock} style={{ fontSize: '1.2rem' }} />
                                        </InputAdornment>
                                    ) :
                                        null
                            }}
                            autoComplete='password'
                            type={type === 'password' ? !showPassword ? 'password' : 'text' : type}
                            stateColor={fieldState?.state}
                            color={fieldState?.state}
                            helperText={fieldState?.message}
                            disabled={!!disabled}
                            label={secondaryLabel ?? ""}
                            fullWidth
                            size='small'
                            value={value}
                            onChange={onChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                }
            </StyledTableCell>
        </TableRow>
    )
}

const StyledConfirmButton = styled(Button)(({ theme }) => ({
    maxWidth: '100px',
    minWidth: '100px',
    minHeight: '40px',
    maxHeight: '40px',
    backgroundColor: theme.palette.primary.main,
    color: grey[100],
    marginLeft: '30px',

    "&:hover": {
        backgroundColor: theme.palette.primary.main,
        boxShadow: '0px 0px 6px 1px rgba(75,90,35,0.5)'
    },

    "&:active": {
        backgroundColor: theme.palette.secondary.main
    }
}));

const RegisterGroupPage: FC = () => {
    // -- Hooks -- 
    const client = useServiceStackClient();
    const navigate = useNavigate()

    // -- Context --
    const { refresh } = useContext(AuthContext);
    const { addAlert } = useContext(AlertContext);
    const { user } = useContext(AuthContext);
    const { species: { species } } = useContext(SpeciesContext);
    const { species: speciesName } = useParams();

    // -- Group Registration state --
    const [groupRegistrationDetails, setGroupRegistrationDetails] = useState<PostCommunityGroupRegistration>(
        new PostCommunityGroupRegistration({
            groupName: '',
            adminUsername: '',
            email: '',
            autoLogin: false
        })
    );
    const [groupRegistrationDetailsState, setGroupRegistrationDetailsState] = useState<Record<string, FieldState>>({});
    const [loading, setLoading] = useState<boolean>(false);

    // -- Password state --
    const [password, setPassword] = useState<{ password: string, confirmPassword: string }>({
        password: '',
        confirmPassword: ''
    });
    const [passwordState, setPasswordState] = useState<Record<string, FieldState>>({});


    // Validate the forgot passsword fields
    const validateGroupRegistrationField = useCallback((field: string, value: any, warn?: boolean): boolean => {
        // Confirm the field is a key of user registration type
        if (field as keyof PostCommunityGroupRegistration) {
            // Update group registration name
            if (field === 'groupName') {
                const valid = value && value !== '';

                if (!valid) {
                    setGroupRegistrationDetailsState(c => {
                        return {
                            ...c,
                            [field]: {
                                state: 'error',
                                message: 'Group Name is required.'
                            }
                        };
                    });
                }
                else {
                    setGroupRegistrationDetailsState(c => {
                        const newState = { ...c };
                        delete newState[field];
                        return newState;
                    });
                }

                return valid;
            }
            // Update user registration username
            else if (field === 'adminUsername') {
                const valid = value && value !== '';

                if (!valid) {
                    setGroupRegistrationDetailsState(c => {
                        return {
                            ...c,
                            [field]: {
                                state: 'error',
                                message: 'Admin Username is required.'
                            }
                        };
                    });
                }
                else {
                    setGroupRegistrationDetailsState(c => {
                        delete c[field];
                        return c;
                    })
                }

                return valid;
            }
            // Update user registration email
            else if (field === 'email') {
                const regex = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/

                const valid = regex.test(value);
                const errorMessage = !value || value === '' ?
                    'Email is required.' :
                    'Invalid email.'

                if (!valid) {
                    if (warn) {
                        setGroupRegistrationDetailsState(c => {
                            return {
                                ...c,
                                [field]: {
                                    state: 'warning',
                                    message: 'Invalid email'
                                }
                            };
                        });
                    }
                    else {
                        setGroupRegistrationDetailsState(c => {
                            return {
                                ...c,
                                [field]: {
                                    state: 'error',
                                    message: errorMessage
                                }
                            };
                        });
                    }
                }
                else {
                    setGroupRegistrationDetailsState(c => {
                        delete c[field];
                        return c;
                    })
                }

                return valid;
            }
            // Otherwise, clear all errors/warnings
            else {
                setGroupRegistrationDetailsState({});
            }

            return true;
        }

        return false;

    }, [setGroupRegistrationDetailsState])

    const handleGroupRegistrationChange = useCallback((field: string, value: any) => {
        validateGroupRegistrationField(field, value, true);

        setGroupRegistrationDetails(c => {
            return new PostCommunityGroupRegistration({
                ...c,
                [field]: value
            });
        })
    }, [validateGroupRegistrationField, setGroupRegistrationDetails]);

    const handlePasswordChange = useCallback((field: string, value: string) => {
        setPassword(c => {
            return {
                ...c,
                [field]: value
            };
        })
    }, [setPassword]);

    const validatePassword = useCallback(() => {
        const passwordRegex = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/;

        if (!passwordRegex.test(password.password)) {
            setPasswordState({
                ['password']: {
                    message: 'Password strength does not meet requirements. Password must have a minimum of 8 characters, including at least 1 uppercase character, 1 lowercase character and one special character: [#?!@$%^&*-].',
                    state: 'error'
                },
                ['confirmPassword']: {
                    message: undefined,
                    state: 'error'
                }
            });

            return false;
        }
        if (password.password !== password.confirmPassword) {
            setPasswordState({
                ['password']: {
                    message: undefined,
                    state: 'error'
                },
                ['confirmPassword']: {
                    message: 'Password fields must be the same.',
                    state: 'error'
                }
            });

            return false;
        }
        else if (password.password === '' || password.confirmPassword === '') {
            let state = {};
            if (password.password === '') {
                state = {
                    ['password']: {
                        message: 'Password is required.',
                        state: 'error'
                    }
                };
            }
            if (password.confirmPassword === '') {
                state = {
                    ...state,
                    ['confirmPassword']: {
                        message: 'Password confirmation is required.',
                        state: 'error'
                    }
                }
            }
            setPasswordState(state);
        }
        else {
            setPasswordState({});
        }

        return true;
    }, [password, setPasswordState])

    const handleGroupRegistrationSubmit = useCallback(async () => {
        if (loading) {
            return;
        }
        setLoading(true);

        if (validateGroupRegistrationField('groupName', groupRegistrationDetails?.groupName) &&
            validateGroupRegistrationField('adminUsername', groupRegistrationDetails?.adminUsername) &&
            validateGroupRegistrationField('email', groupRegistrationDetails?.email) &&
            validatePassword()) {
            try {
                // Make request
                await client.post(
                    new PostCommunityGroupRegistration({
                        groupName: groupRegistrationDetails?.groupName,
                        adminUsername: groupRegistrationDetails?.adminUsername,
                        email: groupRegistrationDetails?.email,
                        password: password?.password,
                        autoLogin: groupRegistrationDetails?.autoLogin,
                        species: species
                    })
                );

                // Alert user of success
                addAlert({
                    content: <AlertTitle>
                        Group registration successful.
                    </AlertTitle>,
                    dismissable: true,
                    severity: 'success',
                    timeout: SUCCESS_TIMEOUT
                });

                const doNavigate = groupRegistrationDetails?.autoLogin;

                setGroupRegistrationDetails(new PostCommunityGroupRegistration({
                    groupName: '',
                    adminUsername: '',
                    email: '',
                    autoLogin: false
                }));
                setGroupRegistrationDetailsState({});
                setPassword({ password: '', confirmPassword: '' });
                setPasswordState({});

                // Refresh the auth user
                await refresh();

                // Redirect to the data managment page
                if (doNavigate) {
                    navigate("/" + speciesName + "/datamanagement?submitter=4");
                }
            } catch (error: any) {
                if (error?.responseStatus) {
                    if (error.responseStatus.errorCode === UserFormatError.errorCode) {
                        let newState: Record<string, FieldState> = {};

                        for (var key of Object.keys(error.responseStatus.meta)) {
                            newState[key] = {
                                message: error.responseStatus.meta[key],
                                state: 'error'
                            }
                        }

                        addAlert({
                            content: <AlertTitle>
                                {newState[Object.keys(newState)[0]].message}
                            </AlertTitle>,
                            dismissable: true,
                            severity: 'error',
                            timeout: ERROR_TIMEOUT
                        });

                        setGroupRegistrationDetailsState(newState);
                    }
                    else {
                        addAlert({
                            content: <AlertTitle>
                                {error.responseStatus.message}
                            </AlertTitle>,
                            dismissable: true,
                            severity: 'error',
                            timeout: ERROR_TIMEOUT
                        });
                    }
                }
                else {
                    addAlert({
                        content: <AlertTitle>
                            {error.message}
                        </AlertTitle>,
                        dismissable: true,
                        severity: 'error',
                        timeout: ERROR_TIMEOUT
                    });
                }
            }
        }
        setLoading(false);
    }, [
        client,
        groupRegistrationDetails,
        password,
        validateGroupRegistrationField,
        validatePassword,
        addAlert,
        setGroupRegistrationDetails,
        setGroupRegistrationDetailsState,
        loading,
        setLoading
    ]);

    if (user) {
        return (
            <RegisterGroupContainer>
                <Typography color="#333" variant='h2'>Create a new group</Typography>
                <Hr color={grey[400]} sx={{ margin: '40px 0' }} />
                <Table>
                    <TableBody>
                        <StyledTableRow
                            primaryLabel="Group Name"
                            fieldState={groupRegistrationDetailsState['groupName']}
                            value={groupRegistrationDetails?.groupName}
                            onChange={(v) => handleGroupRegistrationChange('groupName', v.target.value)}
                        />
                        <StyledTableRow
                            primaryLabel="Admin Username"
                            fieldState={groupRegistrationDetailsState['adminUsername']}
                            value={groupRegistrationDetails?.adminUsername}
                            onChange={(v) => handleGroupRegistrationChange('adminUsername', v.target.value)}
                        />
                        <StyledTableRow
                            primaryLabel="Email Address"
                            fieldState={groupRegistrationDetailsState['email']}
                            value={groupRegistrationDetails?.email}
                            onChange={(v) => handleGroupRegistrationChange('email', v.target.value)}
                        />
                        <StyledTableRow
                            type="password"
                            primaryLabel="Password"
                            fieldState={passwordState['password']}
                            value={password.password}
                            onChange={(v) => handlePasswordChange('password', v.target.value)}
                        />
                        <StyledTableRow
                            type="password"
                            primaryLabel="Confirm Password"
                            fieldState={passwordState['confirmPassword']}
                            value={password.confirmPassword}
                            onChange={(v) => handlePasswordChange('confirmPassword', v.target.value)}
                        />
                    </TableBody>
                </Table>
                <div>
                    <Checkbox
                        checked={!!groupRegistrationDetails?.autoLogin}
                        onChange={(v) => handleGroupRegistrationChange('autoLogin', v.target.checked)}
                    />
                    <Typography display='inline'>Automatically login as group admin</Typography>
                </div>

                <Center ha='end' m='15px 10px'>
                    <StyledConfirmButton
                        onClick={handleGroupRegistrationSubmit}
                        disabled={loading}
                    >
                        <Box display="flex" alignItems="center" justifyContent="center" width="100%">
                            {loading ? (
                                <CircularProgress sx={{ color: "white" }} size={20} thickness={5} />
                            ) : (
                                <Typography fontWeight='600'>Submit</Typography>
                            )}
                        </Box>
                    </StyledConfirmButton>
                </Center>

            </RegisterGroupContainer>
        )
    }
    else {
        return (
            <RegisterGroupContainer>
                <Typography color="#333" variant='h2'>Create a new group</Typography>
                <Hr color={grey[400]} sx={{ margin: '40px 0' }} />
                <Typography
                    sx={{
                        padding: '10px'
                    }}
                >
                    Please sign in under a regular account if you wish to create a group.
                </Typography>
            </RegisterGroupContainer>
        )
    }
}

export default RegisterGroupPage